import { useState, useEffect } from 'react';
import { Phone, Mail, Calendar, Clock, Loader2, CheckCircle, AlertCircle, X, User } from 'lucide-react';

const BOOKING_API_BASE = process.env.REACT_APP_BACKEND_URL + '/api/booking';

const BookingModal = ({ isOpen, onClose, preselectedBranch = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    branch: preselectedBranch || '',
    staff_id: '',
    date: '',
    time: '',
  });

  // API data state
  const [branches, setBranches] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  // Fetch branches on mount
  useEffect(() => {
    if (isOpen) {
      fetchBranches();
      // Reset form when modal opens
      setFormData(prev => ({
        ...prev,
        name: '',
        phone: '',
        email: '',
        branch: preselectedBranch || '',
        staff_id: '',
        date: '',
        time: '',
      }));
      setBookingResult(null);
      setStaffList([]);
      setAvailableSlots([]);
    }
  }, [isOpen, preselectedBranch]);

  // Fetch staff when branch changes
  useEffect(() => {
    if (formData.branch) {
      fetchStaff(formData.branch);
      setFormData(prev => ({ ...prev, staff_id: '', date: '', time: '' }));
      setAvailableSlots([]);
    }
  }, [formData.branch]);

  // Fetch slots when date changes
  useEffect(() => {
    if (formData.date && formData.branch) {
      fetchAvailableSlots();
    }
  }, [formData.date, formData.branch]);

  const fetchBranches = async () => {
    setLoadingBranches(true);
    try {
      const response = await fetch(`${BOOKING_API_BASE}/branches`);
      if (response.ok) {
        const data = await response.json();
        setBranches(data.filter(b => b.available) || []);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
    } finally {
      setLoadingBranches(false);
    }
  };

  const fetchStaff = async (branchId) => {
    setLoadingStaff(true);
    try {
      const response = await fetch(`${BOOKING_API_BASE}/staff/${branchId}`);
      if (response.ok) {
        const data = await response.json();
        setStaffList(data || []);
      }
    } catch (err) {
      console.error('Error fetching staff:', err);
      setStaffList([]);
    } finally {
      setLoadingStaff(false);
    }
  };

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    setAvailableSlots([]);
    setFormData(prev => ({ ...prev, time: '' }));
    try {
      const response = await fetch(`${BOOKING_API_BASE}/available-slots/${formData.branch}/${formData.date}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots || []);
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setBookingResult(null);

    try {
      const response = await fetch(`${BOOKING_API_BASE}/book-appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email || '',
          branch: formData.branch,
          date: formData.date,
          time: formData.time,
          staff_id: formData.staff_id || '',
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setBookingResult({
          success: true,
          message: data.message,
          appointmentId: data.appointment_id,
        });
      } else {
        setBookingResult({
          success: false,
          message: data.message || data.detail || 'Failed to book appointment. Please try again.',
        });
      }
    } catch (err) {
      setBookingResult({
        success: false,
        message: 'Network error. Please check your connection and try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div 
        className="bg-white w-full max-w-lg p-8 relative max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl"
        data-testid="booking-modal"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-black/50 hover:text-black transition-colors"
          data-testid="close-booking-modal"
        >
          <X size={24} strokeWidth={1.5} />
        </button>

        <h2 className="text-black text-2xl font-light tracking-wide mb-2">Book Appointment</h2>
        <p className="text-black/60 text-sm mb-8">BLR Nail Club</p>

        {/* Success State */}
        {bookingResult?.success ? (
          <div className="text-center py-8">
            <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
            <h3 className="text-green-600 text-xl font-medium mb-2">Booking Confirmed!</h3>
            <p className="text-black/70 mb-4">{bookingResult.message}</p>
            {bookingResult.appointmentId && (
              <p className="text-black/50 text-sm">Appointment ID: {bookingResult.appointmentId}</p>
            )}
            <button
              onClick={onClose}
              className="mt-6 bg-[#B8860B] text-black px-8 py-3 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#9A7B0A] transition-colors"
            >
              Close
            </button>
          </div>
        ) : bookingResult?.success === false ? (
          /* Error State */
          <div className="text-center py-8">
            <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
            <h3 className="text-red-600 text-xl font-medium mb-2">Booking Failed</h3>
            <p className="text-black/70 mb-4">{bookingResult.message}</p>
            <button
              onClick={() => setBookingResult(null)}
              className="mt-6 bg-[#B8860B] text-black px-8 py-3 text-sm tracking-[0.15em] uppercase font-medium hover:bg-[#9A7B0A] transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div>
              <label className="block text-black/50 text-[10px] tracking-[0.15em] uppercase mb-2">
                <User size={14} className="inline mr-2" />
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter your name"
                className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-black text-sm font-light focus:outline-none focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] transition-colors"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-black/50 text-[10px] tracking-[0.15em] uppercase mb-2">
                <Phone size={14} className="inline mr-2" />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="Enter your phone number"
                className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-black text-sm font-light focus:outline-none focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] transition-colors"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-black/50 text-[10px] tracking-[0.15em] uppercase mb-2">
                <Mail size={14} className="inline mr-2" />
                Email Address (Optional)
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-black text-sm font-light focus:outline-none focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] transition-colors"
              />
            </div>

            {/* Branch Selection */}
            <div>
              <label className="block text-black/50 text-[10px] tracking-[0.15em] uppercase mb-2">
                Select Location *
              </label>
              <div className="relative">
                <select
                  name="branch"
                  value={formData.branch}
                  onChange={handleInputChange}
                  required
                  disabled={loadingBranches}
                  className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-black text-sm font-light focus:outline-none focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] transition-colors cursor-pointer appearance-none"
                >
                  <option value="">Select Branch</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                {loadingBranches && (
                  <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
                )}
              </div>
            </div>

            {/* Staff Selection - REQUIRED */}
            <div>
              <label className="block text-black/50 text-[10px] tracking-[0.15em] uppercase mb-2">
                Select Staff *
              </label>
              <div className="relative">
                <select
                  name="staff_id"
                  value={formData.staff_id}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.branch || loadingStaff}
                  className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-black text-sm font-light focus:outline-none focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] transition-colors cursor-pointer appearance-none disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">
                    {!formData.branch 
                      ? 'Select branch first' 
                      : loadingStaff 
                        ? 'Loading staff...' 
                        : staffList.length === 0 
                          ? 'No staff available'
                          : 'Select Staff'}
                  </option>
                  {staffList.map((staff) => (
                    <option key={staff.id} value={staff.id}>
                      {staff.name}
                    </option>
                  ))}
                </select>
                {loadingStaff && (
                  <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
                )}
              </div>
              {formData.branch && !loadingStaff && staffList.length === 0 && (
                <p className="text-red-500 text-xs mt-1">No staff available at this location.</p>
              )}
            </div>

            {/* Date */}
            <div>
              <label className="block text-black/50 text-[10px] tracking-[0.15em] uppercase mb-2">
                <Calendar size={14} className="inline mr-2" />
                Preferred Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
                disabled={!formData.branch}
                min={getTodayDate()}
                className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-black text-sm font-light focus:outline-none focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] transition-colors disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>

            {/* Time */}
            <div>
              <label className="block text-black/50 text-[10px] tracking-[0.15em] uppercase mb-2">
                <Clock size={14} className="inline mr-2" />
                Preferred Time *
              </label>
              <div className="relative">
                <select
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.date || loadingSlots}
                  className="w-full bg-gray-50 border border-gray-200 rounded px-4 py-3 text-black text-sm font-light focus:outline-none focus:border-[#B8860B] focus:ring-1 focus:ring-[#B8860B] transition-colors cursor-pointer appearance-none disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">
                    {!formData.branch
                      ? 'Select branch first'
                      : !formData.date
                        ? 'Select date first'
                        : loadingSlots
                          ? 'Loading slots...'
                          : availableSlots.length === 0
                            ? 'No slots available'
                            : 'Select Time'}
                  </option>
                  {availableSlots.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
                {loadingSlots && (
                  <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
                )}
              </div>
              {formData.date && !loadingSlots && availableSlots.length === 0 && formData.branch && (
                <p className="text-red-500 text-xs mt-1">No slots available for this date. Please select another date.</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || !formData.name || !formData.phone || !formData.branch || !formData.staff_id || !formData.date || !formData.time}
              className="w-full bg-[#B8860B] text-black py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#9A7B0A] transition-colors mt-6 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Booking...
                </>
              ) : (
                'Confirm Booking'
              )}
            </button>

            <p className="text-black/40 text-xs text-center mt-4">
              Your appointment will be confirmed instantly
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
